import React from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScoreCardMetric {
  label: string;
  value: string | number;
  target?: string | number;
  unit?: string;
  status?: 'green' | 'amber' | 'red' | 'neutral';
  description?: string;
}

interface ScoreCardProps {
  title?: string;
  metrics: ScoreCardMetric[];
  columns?: 2 | 3 | 4;
}

// ---------------------------------------------------------------------------
// Status border colors
// ---------------------------------------------------------------------------

const STATUS_BORDER: Record<string, string> = {
  green: 'border-l-4 border-l-emerald-500',
  amber: 'border-l-4 border-l-amber-500',
  red: 'border-l-4 border-l-red-500',
  neutral: 'border-l-4 border-l-gray-300',
};

// ---------------------------------------------------------------------------
// Grid column classes
// ---------------------------------------------------------------------------

const GRID_COLS: Record<number, string> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ScoreCard: React.FC<ScoreCardProps> = ({
  title,
  metrics,
  columns = 3,
}) => {
  if (!metrics || metrics.length === 0) {
    return null;
  }

  const gridColsClass = GRID_COLS[columns] || GRID_COLS[3];

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden my-8">
      {/* Optional title header */}
      {title && (
        <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-800 m-0">
            {title}
          </h3>
        </div>
      )}

      {/* Metric cards grid */}
      <div className={`p-5 grid grid-cols-1 sm:grid-cols-2 ${gridColsClass} gap-4`}>
        {metrics.map((metric, index) => {
          const borderClass = metric.status ? STATUS_BORDER[metric.status] : '';

          return (
            <div
              key={index}
              className={[
                'bg-white rounded-lg border border-gray-200 p-4',
                borderClass,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {/* Label */}
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {metric.label}
              </div>

              {/* Value */}
              <div className="mt-1 text-2xl font-bold text-gray-900">
                {metric.value}
                {metric.unit && (
                  <span className="text-sm font-medium text-gray-500">
                    {metric.unit}
                  </span>
                )}
              </div>

              {/* Target */}
              {metric.target !== undefined && (
                <div className="mt-1 text-xs text-gray-400">
                  Target: {metric.target}
                  {metric.unit ?? ''}
                </div>
              )}

              {/* Description */}
              {metric.description && (
                <div className="mt-1 text-xs text-gray-500">
                  {metric.description}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScoreCard;
